// ═══════════════════════════════════════════════════════════════
// SORT UTILITIES
// Pure functions for sorting tasks by various criteria
// ═══════════════════════════════════════════════════════════════

import { getAllConditionTaskIds } from '../../shared/ConditionsDropdown/utils/conditionHelpers';

/**
 * getUpstreamTaskIds - Extract dependency task IDs from a task
 * Uses unlockConditions (v1 or v2) or fallback to dependsOn (legacy)
 */
const getUpstreamTaskIds = (task) => {
  const ids = new Set();
  
  const conditionIds = getAllConditionTaskIds(task.unlockConditions);
  for (const id of conditionIds) {
    ids.add(id);
  }
  
  if (ids.size === 0 && Array.isArray(task.dependsOn)) {
    for (const dep of task.dependsOn) {
      const depId = typeof dep === 'string' ? dep : dep?.taskId;
      if (depId) {
        ids.add(depId);
      }
    }
  }
  
  return ids;
};

/**
 * topologicalSort - Sort tasks so dependencies come before dependents
 * Uses Kahn's algorithm for stable topological sorting
 * Tasks with no dependencies come first, then tasks that depend on them, etc.
 */
const topologicalSort = (tasks) => {
  if (!tasks || tasks.length === 0) return tasks;
  
  const taskMap = new Map(tasks.map(t => [t.id, t]));
  const taskIds = new Set(tasks.map(t => t.id));
  
  // Build adjacency list and in-degree count
  // edge: upstream -> downstream (if B depends on A, then A -> B)
  const downstream = new Map(); // taskId -> Set of tasks that depend on it
  const inDegree = new Map();   // taskId -> number of dependencies within this set
  
  for (const task of tasks) {
    downstream.set(task.id, new Set());
    inDegree.set(task.id, 0);
  }
  
  // Build the graph
  for (const task of tasks) {
    const upstreamIds = getUpstreamTaskIds(task);
    for (const upstreamId of upstreamIds) {
      // Only count dependencies that exist in our task set
      if (taskIds.has(upstreamId)) {
        downstream.get(upstreamId).add(task.id);
        inDegree.set(task.id, inDegree.get(task.id) + 1);
      }
    }
  }
  
  // Kahn's algorithm: start with tasks that have no dependencies
  const queue = [];
  const result = [];
  
  // Initialize queue with tasks that have no in-edges (no dependencies)
  for (const task of tasks) {
    if (inDegree.get(task.id) === 0) {
      queue.push(task.id);
    }
  }
  
  // Sort initial queue for stability (by name or createdAt)
  queue.sort((a, b) => {
    const taskA = taskMap.get(a);
    const taskB = taskMap.get(b);
    return (taskA?.name || '').localeCompare(taskB?.name || '');
  });
  
  while (queue.length > 0) {
    const currentId = queue.shift();
    const currentTask = taskMap.get(currentId);
    if (currentTask) {
      result.push(currentTask);
    }
    
    // Get downstream tasks and reduce their in-degree
    const dependents = downstream.get(currentId) || new Set();
    const readyDependents = [];
    
    for (const dependentId of dependents) {
      const newDegree = inDegree.get(dependentId) - 1;
      inDegree.set(dependentId, newDegree);
      if (newDegree === 0) {
        readyDependents.push(dependentId);
      }
    }
    
    // Sort newly ready dependents for stability
    readyDependents.sort((a, b) => {
      const taskA = taskMap.get(a);
      const taskB = taskMap.get(b);
      return (taskA?.name || '').localeCompare(taskB?.name || '');
    });
    
    queue.push(...readyDependents);
  }
  
  // Handle any remaining tasks (cycles or orphans)
  // These get appended at the end, sorted by name
  const resultIds = new Set(result.map(t => t.id));
  const remaining = tasks.filter(t => !resultIds.has(t.id));
  remaining.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  
  return [...result, ...remaining];
};

/**
 * getPriorityValue - Convert priority string to numeric value for sorting
 */
const getPriorityValue = (priority) => {
  switch (priority) {
    case 'high': return 3;
    case 'medium': return 2;
    case 'low': return 1;
    default: return 0; // 'none' or undefined
  }
};

/**
 * sortByCustomField - Sort tasks by a custom field value
 */
const sortByCustomField = (tasks, fieldId, field, direction) => {
  if (!field) return tasks;
  
  const sorted = [...tasks];
  const isAsc = direction === 'asc';
  
  // Build option order map for select fields
  const optionOrderMap = {};
  if (field.type === 'select' && field.options) {
    field.options.forEach((opt, index) => {
      optionOrderMap[opt.id] = index;
    });
  }
  
  return sorted.sort((a, b) => {
    const valueA = a.customFieldValues?.[fieldId];
    const valueB = b.customFieldValues?.[fieldId];
    
    // Handle null/undefined - always sort to end
    const aEmpty = valueA === null || valueA === undefined || valueA === '';
    const bEmpty = valueB === null || valueB === undefined || valueB === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1; // Empty values go to end
    if (bEmpty) return -1;
    
    let comparison = 0;
    
    switch (field.type) {
      case 'text':
      case 'url':
        comparison = String(valueA).localeCompare(String(valueB));
        break;
        
      case 'number':
        comparison = (Number(valueA) || 0) - (Number(valueB) || 0);
        break;
        
      case 'checkbox':
        // Boolean comparison: true (checked) = 1, false = 0
        const boolA = valueA === true ? 1 : 0;
        const boolB = valueB === true ? 1 : 0;
        comparison = boolA - boolB;
        break;
        
      case 'select':
        // Sort by option order as defined in field.options
        const orderA = optionOrderMap[valueA] ?? Infinity;
        const orderB = optionOrderMap[valueB] ?? Infinity;
        comparison = orderA - orderB;
        break;
        
      case 'date':
        const dateA = valueA ? new Date(valueA).getTime() : Infinity;
        const dateB = valueB ? new Date(valueB).getTime() : Infinity;
        comparison = dateA - dateB;
        break;
        
      default:
        comparison = 0;
    }
    
    return isAsc ? comparison : -comparison;
  });
};

/**
 * sortTasks - Apply sorting to a list of tasks
 * @param {Array} tasks - List of tasks to sort
 * @param {string} sortSetting - Sort option ID (e.g., 'priority-high', 'custom-fieldId-asc')
 * @param {Array} customFields - Custom field definitions (required for custom field sorting)
 */
export const sortTasks = (tasks, sortSetting, customFields = []) => {
  if (!tasks || tasks.length === 0) return tasks;
  if (sortSetting === 'manual') return tasks;

  // Check if sorting by custom field
  if (sortSetting?.startsWith('custom-')) {
    const match = sortSetting.match(/^custom-(.+)-(asc|desc)$/);
    if (match) {
      const [, fieldId, direction] = match;
      const field = customFields.find(f => f.id === fieldId);
      if (field) {
        return sortByCustomField(tasks, fieldId, field, direction);
      }
    }
    return tasks; // Invalid custom field sort, return unsorted
  }

  const sorted = [...tasks];
  
  switch (sortSetting) {
    case 'dependency':
      return topologicalSort(tasks);
    
    case 'priority-high':
      return sorted.sort((a, b) => {
        const priorityA = getPriorityValue(a.priority);
        const priorityB = getPriorityValue(b.priority);
        return priorityB - priorityA; // High to low
      });
    
    case 'priority-low':
      return sorted.sort((a, b) => {
        const priorityA = getPriorityValue(a.priority);
        const priorityB = getPriorityValue(b.priority);
        return priorityA - priorityB; // Low to high
      });
    
    case 'name-asc':
      return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    
    case 'name-desc':
      return sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
    
    case 'created-newest':
      return sorted.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });
    
    case 'created-oldest':
      return sorted.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : Infinity;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : Infinity;
        return dateA - dateB;
      });
    
    case 'due-date-asc':
      return sorted.sort((a, b) => {
        const dateA = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const dateB = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        return dateA - dateB;
      });
    
    case 'due-date-desc':
      return sorted.sort((a, b) => {
        const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        return dateB - dateA;
      });
    
    case 'updated-newest':
      return sorted.sort((a, b) => {
        const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return dateB - dateA;
      });
    
    case 'updated-oldest':
      return sorted.sort((a, b) => {
        const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : Infinity;
        const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : Infinity;
        return dateA - dateB;
      });
    
    default:
      return tasks;
  }
};
